import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Award, Users, Zap, Target, Globe } from 'lucide-react';
import { mockData } from '../data/mock';
import { revealVariants, staggerContainer } from '../utils/motion';
import { useInViewOnce } from '../hooks/useInViewOnce';

const advantages = [
  {
    icon: Shield,
    title: 'Proven Track Record',
    description: 'Over 500+ successful projects across multiple industries with consistent delivery excellence.',
    metric: '98% Success Rate'
  },
  {
    icon: Users,
    title: 'Expert Team',
    description: 'Seasoned professionals with deep industry knowledge and certifications in their respective fields.',
    metric: '15+ Years Average Experience'
  },
  {
    icon: Zap,
    title: 'Agile Methodology',
    description: 'Flexible and adaptive approach that responds to changing requirements and market conditions.',
    metric: '40% Faster Delivery'
  },
  {
    icon: Globe,
    title: 'Multi-Sector Expertise',
    description: 'Unique advantage of cross-industry insights that bring innovative solutions to traditional challenges.',
    metric: '5 Core Sectors'
  },
  {
    icon: Target,
    title: 'Measurable Results',
    description: 'Focus on delivering quantifiable outcomes that directly impact your business objectives.',
    metric: '25% Average ROI Improvement'
  },
  {
    icon: Award,
    title: 'Quality Assurance',
    description: 'Rigorous quality control processes ensuring excellence in every deliverable and milestone.',
    metric: '100% Quality Standards'
  }
];

export const WhySAX = () => {
  const [ref, isInView] = useInViewOnce();
  const [advantagesRef, advantagesInView] = useInViewOnce();
  const [processRef, processInView] = useInViewOnce();

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-transparent to-slate-50" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="text-center max-w-4xl mx-auto"
          >
            <motion.div
              variants={revealVariants}
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#FF5C8A]/10 to-[#FF8C42]/10 text-[#1F3C88] px-4 py-2 rounded-full text-sm font-medium mb-8"
            >
              <span>Why Choose SAX</span>
            </motion.div>

            <motion.h1 
              variants={revealVariants}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              The <span className="bg-gradient-to-r from-[#1F3C88] to-[#FF5C8A] bg-clip-text text-transparent">SAX Advantage</span>
            </motion.h1>

            <motion.p 
              variants={revealVariants}
              className="text-xl text-gray-600 leading-relaxed mb-12"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Discover why leading organizations trust SAX for their most critical projects. 
              Our unique multi-sector approach and proven methodology deliver results that exceed expectations.
            </motion.p>

            <motion.div 
              variants={revealVariants}
              className="grid grid-cols-2 md:grid-cols-4 gap-6"
            >
              {mockData.stats.map((stat, index) => (
                <div key={index} className="text-center bg-white/80 rounded-2xl p-6 backdrop-blur-sm">
                  <div className="text-3xl font-bold text-[#1F3C88]">{stat.value}</div>
                  <div className="text-sm text-gray-600 mt-1">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Multi-Sector Advantage */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            ref={ref}
            variants={staggerContainer}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="text-center mb-16"
          >
            <motion.h2 
              variants={revealVariants}
              className="text-3xl md:text-4xl font-bold text-gray-900 mb-6"
            >
              Multi-Sector <span className="bg-gradient-to-r from-[#1F3C88] to-[#FF5C8A] bg-clip-text text-transparent">Advantage</span>
            </motion.h2>
            
            <motion.p 
              variants={revealVariants}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              Our cross-industry expertise allows us to bring innovative solutions from one sector to another, 
              creating unique competitive advantages for our clients.
            </motion.p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {advantages.map((advantage, index) => {
              const IconComponent = advantage.icon;
              return (
                <motion.div
                  key={index}
                  variants={revealVariants}
                  className="group"
                >
                  <div className="bg-gradient-to-br from-white to-slate-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 h-full">
                    <div className="flex items-center justify-between mb-6">
                      <div className="w-12 h-12 bg-gradient-to-r from-[#1F3C88] to-[#FF5C8A] rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                        <IconComponent size={24} className="text-white" />
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-[#FF5C8A]">{advantage.metric}</div>
                      </div>
                    </div>
                    
                    <h3 className="text-xl font-bold text-gray-900 mb-4">{advantage.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{advantage.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Process Excellence */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto px-4">
          <motion.div
            ref={processRef}
            variants={staggerContainer}
            initial="hidden"
            animate={processInView ? "visible" : "hidden"}
            className="text-center mb-16"
          >
            <motion.h2 
              variants={revealVariants}
              className="text-3xl md:text-4xl font-bold text-gray-900 mb-6"
            >
              Our Proven <span className="bg-gradient-to-r from-[#1F3C88] to-[#FF5C8A] bg-clip-text text-transparent">Process</span>
            </motion.h2>
            
            <motion.p 
              variants={revealVariants}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              A systematic approach that ensures consistent quality, timely delivery, and measurable outcomes.
            </motion.p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate={processInView ? "visible" : "hidden"}
            className="relative"
          >
            {/* Connection Line */}
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-[#1F3C88] via-[#FF5C8A] to-[#FF8C42] transform -translate-y-1/2 rounded-full" />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {mockData.process.map((step, index) => (
                <motion.div
                  key={step.step}
                  variants={revealVariants}
                  className="relative group text-center"
                >
                  <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 relative z-10">
                    {/* Step Number */}
                    <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 w-12 h-12 bg-gradient-to-r from-[#FF5C8A] to-[#FF8C42] rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                      {step.step}
                    </div>
                    
                    <div className="pt-8">
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">{step.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Case Study Teasers */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            ref={advantagesRef}
            variants={staggerContainer}
            initial="hidden"
            animate={advantagesInView ? "visible" : "hidden"}
            className="text-center mb-16"
          >
            <motion.h2 
              variants={revealVariants}
              className="text-3xl md:text-4xl font-bold text-gray-900 mb-6"
            >
              Success <span className="bg-gradient-to-r from-[#1F3C88] to-[#FF5C8A] bg-clip-text text-transparent">Stories</span>
            </motion.h2>
            
            <motion.p 
              variants={revealVariants}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              Real results from real clients across diverse industries and project types.
            </motion.p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate={advantagesInView ? "visible" : "hidden"}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {mockData.testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                variants={revealVariants}
                className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl p-8 border border-gray-100"
              >
                <div className="text-4xl font-bold text-[#1F3C88] mb-4">
                  {mockData.services[index]?.kpis[0]?.value || "95%"}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {testimonial.company}
                </h3>
                <p className="text-gray-600 mb-4 italic">"{testimonial.text}"</p>
                <div className="text-sm text-gray-500">
                  - {testimonial.name}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#1F3C88] to-[#2d4ba3] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-white/10" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate={advantagesInView ? "visible" : "hidden"}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.h2 
              variants={revealVariants}
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6"
            >
              Ready to Experience the SAX Difference?
            </motion.h2>
            
            <motion.p 
              variants={revealVariants}
              className="text-xl text-blue-100 mb-12 max-w-2xl mx-auto leading-relaxed"
            >
              Join the growing number of organizations that have achieved exceptional results with our multi-sector expertise.
            </motion.p>
            
            <motion.div 
              variants={revealVariants}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link
                to="/request-service"
                className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#FF5C8A] to-[#FF8C42] text-white px-8 py-4 rounded-xl font-semibold hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
              >
                <span>Start Your Project</span>
                <ArrowRight size={20} />
              </Link>

              <Link
                to="/contact"
                className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/30 transition-all duration-300 border border-white/30"
              >
                <span>Schedule Consultation</span>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};